// @ts-nocheck
import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { useAdminToast } from './useAdminToast';

interface AdminImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: any[]) => Promise<void>;
  acceptedFormats?: string[];
  sampleData?: any[];
  title?: string;
}

export const AdminImportDialog: React.FC<AdminImportDialogProps> = ({
  isOpen,
  onClose,
  onImport,
  acceptedFormats = ['.csv', '.json'],
  sampleData,
  title = 'Import Data',
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useAdminToast();

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setPreview([]);
    setErrors([]);

    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'csv') {
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            setErrors(results.errors.map((e) => e.message || 'Parse error'));
          } else {
            setPreview((results.data as any[]).slice(0, 5)); // Preview first 5 rows
          }
        },
        error: (error) => {
          setErrors([error.message || 'Failed to parse CSV']);
        },
      });
    } else if (fileExtension === 'json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target?.result as string);
          const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
          setPreview(dataArray.slice(0, 5)); // Preview first 5 rows
        } catch (err) {
          setErrors(['Invalid JSON format']);
        }
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    setErrors([]);

    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      let data: any[] = [];

      if (fileExtension === 'csv') {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.errors.length > 0) {
              setErrors(results.errors.map((e) => e.message || 'Parse error'));
              setLoading(false);
            } else {
              data = results.data as any[];
              proceedWithImport(data);
            }
          },
          error: (error) => {
            setErrors([error.message || 'Failed to parse CSV']);
            setLoading(false);
          },
        });
      } else if (fileExtension === 'json') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const jsonData = JSON.parse(e.target?.result as string);
            data = Array.isArray(jsonData) ? jsonData : [jsonData];
            proceedWithImport(data);
          } catch (err) {
            setErrors(['Invalid JSON format']);
            setLoading(false);
          }
        };
        reader.readAsText(file);
      }
    } catch (err: any) {
      showError(err.message || 'Failed to import file');
      setLoading(false);
    }
  };

  const proceedWithImport = async (data: any[]) => {
    try {
      await onImport(data);
      showSuccess(`Successfully imported ${data.length} record(s)`);
      handleClose();
    } catch (err: any) {
      showError(err.message || 'Failed to import data');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const downloadSample = () => {
    if (!sampleData || sampleData.length === 0) return;

    const csv = Papa.unparse(sampleData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_import.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-dialog-title"
    >
      <div className="bg-white dark:bg-luna-primary rounded-lg shadow-xl p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 id="import-dialog-title" className="text-lg sm:text-xl font-bold text-luna-text-light dark:text-luna-text-dark pr-2">
            {title}
          </h3>
          <button
            onClick={handleClose}
            className="text-luna-text-light/50 dark:text-luna-text-dark/50 hover:text-luna-text-light dark:hover:text-luna-text-dark p-2 -mt-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            aria-label="Close dialog"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="import-file-input" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
              Select File ({acceptedFormats.join(', ')})
            </label>
            <input
              id="import-file-input"
              ref={fileInputRef}
              type="file"
              accept={acceptedFormats.join(',')}
              onChange={handleFileSelect}
              className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] touch-manipulation"
              aria-label={`Select file to import (${acceptedFormats.join(', ')})`}
            />
          </div>

          {sampleData && sampleData.length > 0 && (
            <div>
              <button
                onClick={downloadSample}
                className="text-sm text-luna-secondary hover:underline"
              >
                Download sample CSV template
              </button>
            </div>
          )}

          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm font-medium text-red-800 dark:text-red-400 mb-1">
                Errors:
              </p>
              <ul className="text-xs text-red-700 dark:text-red-300 list-disc list-inside">
                {errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {preview.length > 0 && (
            <div>
              <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                Preview (first 5 rows):
              </p>
              <div className="overflow-x-auto border border-luna-secondary/30 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-luna-secondary/10">
                    <tr>
                      {Object.keys(preview[0]).map((key) => (
                        <th
                          key={key}
                          className="px-3 py-2 text-left text-xs font-semibold text-luna-text-light dark:text-luna-text-dark"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-t border-luna-secondary/10"
                      >
                        {Object.values(row).map((value: any, colIdx) => (
                          <td
                            key={colIdx}
                            className="px-3 py-2 text-luna-text-light dark:text-luna-text-dark"
                          >
                            {String(value || '').substring(0, 50)}
                            {String(value || '').length > 50 && '...'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 mt-2">
                Total rows in file: {file ? 'Processing...' : 'N/A'}
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-luna-secondary/10">
            <button
              onClick={handleClose}
              className="w-full sm:w-auto px-6 py-3 rounded-lg text-luna-text-light dark:text-luna-text-dark hover:bg-gray-100 dark:hover:bg-luna-secondary/10 transition-colors min-h-[44px] touch-manipulation"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!file || loading || errors.length > 0}
              className="w-full sm:w-auto px-6 py-3 rounded-lg bg-luna-secondary text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-luna-secondary/90 min-h-[44px] touch-manipulation"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Importing...
                </span>
              ) : (
                'Import'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

